/**
 * LE BANDEAU DES CONTRÔLES MOBILE PARTNER — les tuiles de l'onglet BL (maquette `isAchats`, bloc `osRows`).
 *
 * Un contrôle scanné en réserve remonte ici tout seul. Un clic : rapprocher d'un BL du même fournisseur,
 * ou créer le BL en donnant son numéro — zéro import, zéro ressaisie. Puis la modale du bon s'ouvre, et
 * c'est là qu'on clique « Entrer les pièces en stock » (décision utilisateur : la tuile VALIDE le bon,
 * elle ne l'intègre pas — le prix se valorise au siège).
 *
 * ⚠️ RELEVÉ SUR LE DESSIN : carte teal-100 bord teal-400 r-md, 12px 16px ; icône téléphone 18px ; titre
 * 13.5/700 « Contrôle {équipement} reçu — {n} pièces scannées » ; sous-ligne 12px ink-700 « {heure} ·
 * fournisseur supposé : {sup} · rapprochement proposé : {BL} (même fournisseur, reçu ce jour) » ou
 * « · aucun BL correspondant — livraison à la volée » ; UN bouton par tuile : « Rapprocher du {BL} »
 * (noir, 36px) ou « Créer le BL directement » (teal, contour). ⚠️ DIVERGENCES DITES : le dessin crée le
 * BL sous le numéro de session ; ici le numéro se TAPE (celui du bordereau fournisseur) — et la ligne
 * des MARQUES des articles connus s'ajoute (demande utilisateur : « si un article est connu, sa marque
 * doit apparaître dans la tuile ») ; « Écarter » est un lien discret (arbitrage A3).
 */
import { useState } from 'react';
import { confirm, showToast } from '@orgazone/ui-web';
import { api, type ReceptionControl } from '../api';
import { MONO } from './receptions-shared';
import { t } from '../i18n';

/** L'heure d'un contrôle — « aujourd'hui à 09:12 », ou la date courte pour un contrôle d'un autre jour. */
export function heureDuControle(ms: number, now: number = Date.now()): string {
  const d = new Date(ms);
  const n = new Date(now);
  const heure = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const memeJour = d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  return memeJour ? t('aujourd’hui à {heure}', { heure }) : t('le {v}/{v2} à {heure}', { v: String(d.getDate()).padStart(2, '0'), v2: String(d.getMonth() + 1).padStart(2, '0'), heure });
}

/** La sous-ligne de la tuile, mot pour mot comme le dessin — et le fournisseur supposé quand il existe. */
export function sousLigneDuControle(c: ReceptionControl, now: number = Date.now()): string {
  const parts = [heureDuControle(c.submittedAt, now)];
  // ⚠️ DEUX PHRASES ENTIÈRES : « inconnu » passé comme valeur restait français dans toutes les langues.
  parts.push(c.supposedSupplier ? t('fournisseur supposé : {v}', { v: c.supposedSupplier.label }) : t('fournisseur supposé : inconnu'));
  parts.push(c.proposedNumber
    ? t('rapprochement proposé : {proposedNumber} (même fournisseur, reçu récemment)', { proposedNumber: c.proposedNumber })
    : t('aucun BL correspondant — livraison à la volée'));
  return parts.join(' · ');
}

/** L'icône téléphone du dessin — un trait, 18px, la couleur du texte. */
function IconeTelephone() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" />
    </svg>
  );
}

export function ControlesMobilePartner({ controls, fraicheur, onSettled, onChanged }: {
  controls: ReceptionControl[];
  /**
   * QUAND L'ÉCRAN A REGARDÉ POUR LA DERNIÈRE FOIS (« vérifié il y a … »).
   * ⚠️ LA PAGE INTERROGE, ELLE N'EST PAS NOTIFIÉE : le back-office n'a aucun canal vers le navigateur.
   * Sans ce repère, une liste vide se lirait « aucun contrôle n'est arrivé » alors qu'elle peut vouloir
   * dire « le rafraîchissement est en panne depuis dix minutes » (§23.0).
   */
  fraicheur: string;
  /** Le bon touché : l'écran l'ouvre pour que l'opérateur clique « Entrer les pièces en stock ». */
  onSettled: (number: string) => void;
  /** Relire les tuiles après un geste (rapprochée, créée, écartée). */
  onChanged: () => void;
}) {
  // ⚠️ RIEN À MONTRER = RIEN DU TOUT, repère compris : un bandeau qui ne porterait que son seul repère de
  //    fraîcheur au-dessus d'une table pleine serait du bruit permanent pour une attente qui, elle, est
  //    rare. Le repère accompagne les tuiles ; il ne les remplace pas.
  if (controls.length === 0) return null;
  return (
    <div data-tour="ach-controles" style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
      {controls.map((c) => <Tuile key={c.zoneId} c={c} onSettled={onSettled} onChanged={onChanged} />)}
      <span style={{ justifySelf: 'end', fontSize: '11.5px', color: 'var(--ink-500)' }}>{fraicheur}</span>
    </div>
  );
}

function Tuile({ c, onSettled, onChanged }: { c: ReceptionControl; onSettled: (n: string) => void; onChanged: () => void }) {
  const [numero, setNumero] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const equipement = c.equipmentCode ?? t('appareil inconnu');

  /**
   * LE GESTE : rapprocher (le bon proposé) ou créer (le numéro tapé) — la même porte. Puis le bon s'ouvre.
   * ⚠️ LE TOAST DIT CE QUI A ÉTÉ FAIT, avec le vrai numéro : « créé » et « rapproché » ne sont pas le
   *    même fait, et un opérateur qui a tapé un numéro existant doit apprendre qu'il a rapproché.
   */
  const regler = async (number: string) => {
    if (occupe) return;
    setOccupe(true);
    setFailure(null);
    try {
      const r = await api.settleControl(c.zoneId, number);
      showToast(r.created ? t('BL {number} créé depuis le contrôle Mobile Partner.', { number: r.number }) : t('Contrôle posé sur le BL {number} — {n} ligne comptée.', { number: r.number, n: r.checked }));
      onChanged();
      onSettled(r.number);
    } catch (e) {
      // ⚠️ Le motif réel, sur la tuile : « déjà rapproché », « bon clôturé » se corrigent différemment.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setOccupe(false);
    }
  };

  const ecarter = async () => {
    // ⚠️ LE BOUTON S'ANNONCE INACTIF PENDANT UN RAPPROCHEMENT (`aria-disabled`) : sans ce garde il agissait quand même, et
    //    écartait une zone qu'un autre geste était en train de poser sur un bon.
    if (occupe) return;
    const ok = await confirm({
      title: t('Écarter ce contrôle ?'),
      message: t('Le contrôle {equipement} ({n} pièce) sortira du bandeau sans rejoindre aucun bon. Le relevé du terminal reste conservé.', { equipement, n: c.pieces }),
      confirmLabel: t('Écarter'), tone: 'danger',
    });
    if (!ok) return;
    setOccupe(true);
    try {
      await api.discardControl(c.zoneId);
      showToast(t('Contrôle écarté.'));
      onChanged();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setOccupe(false);
    }
  };

  const boutonNoir: React.CSSProperties = { height: 36, padding: '0 14px', border: '1px solid transparent', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: occupe ? 'progress' : 'pointer' };
  const boutonTeal: React.CSSProperties = { height: 36, padding: '0 14px', border: '1px solid var(--teal-500)', borderRadius: 'var(--r-sm)', background: 'transparent', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: occupe ? 'progress' : 'pointer' };

  return (
    <div data-testid="controle-mobile-partner" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 'var(--r-md)', background: 'var(--teal-100)', border: '1px solid var(--teal-400)', color: 'var(--teal-800)' }}>
      <IconeTelephone />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>
          {t('Contrôle {equipement} reçu — {n} pièce scannée', { equipement, n: c.pieces })}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--ink-700)', marginTop: 2 }}>{sousLigneDuControle(c)}</div>
        {/* ⚠️ LES MARQUES DES ARTICLES CONNUS : c'est ce que l'opérateur reconnaît d'un coup d'œil — un
            code inconnu du catalogue n'en a pas, et se compte à part plutôt que de s'inventer. */}
        <div data-testid="controle-marques" style={{ fontSize: '12px', color: 'var(--ink-700)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {c.brands.length > 0
            ? c.brands.map((b) => (
              <span key={b} style={{ height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)', background: 'var(--paper)', border: '1px solid var(--teal-300)', fontSize: '11px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', color: 'var(--ink-800)' }}>{b}</span>
            ))
            : <span>{t('aucun article connu du catalogue')}</span>}
          {c.unknownCodes > 0 && (
            <span style={{ color: 'var(--amber-fg)' }}>· {t('{n} code inconnu — il entrera au bon comme fantôme', { n: c.unknownCodes })}</span>
          )}
          {c.label && <span className="mono" style={{ fontFamily: MONO, fontSize: '11px', color: 'var(--ink-500)' }}>· « {c.label} »</span>}
        </div>
        {failure && <div role="alert" style={{ marginTop: 6, fontSize: '12px', color: 'var(--danger-ink)' }}>{failure}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {c.proposedNumber
          ? (
            <button type="button" style={boutonNoir} aria-disabled={occupe || undefined} onClick={() => void regler(c.proposedNumber!)}>
              {t('Rapprocher du {proposedNumber}', { proposedNumber: c.proposedNumber })}
            </button>
          )
          : (
            <>
              {/* ⚠️ LE NUMÉRO SE TAPE : celui du bordereau du fournisseur, qu'on retrouvera sur la facture —
                  jamais un numéro inventé par le serveur (décision utilisateur). */}
              <input
                className="field-input" value={numero} onChange={(e) => setNumero(e.target.value)}
                placeholder={t('N° du BL fournisseur')} aria-label={t('Numéro du bon de livraison — contrôle {equipement}', { equipement })}
                onKeyDown={(e) => { if (e.key === 'Enter' && numero.trim()) void regler(numero); }}
                style={{ height: 36, width: 190, fontFamily: MONO, fontSize: '12.5px' }}
              />
              <button type="button" style={{ ...boutonTeal, opacity: numero.trim() ? 1 : 0.55 }} aria-disabled={occupe || !numero.trim() || undefined}
                title={numero.trim() ? undefined : t('Donnez le numéro du bon : celui du bordereau fournisseur.')}
                onClick={() => { if (numero.trim()) void regler(numero); }}>
                
                {t('Créer le BL directement')}
              </button>
            </>
          )}
        <button type="button" onClick={() => void ecarter()} aria-disabled={occupe || undefined}
          style={{ border: 'none', background: 'none', padding: '0 4px', color: 'var(--ink-500)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
          
          {t('Écarter')}
        </button>
      </div>
    </div>
  );
}
